/**
 * Analytics Dashboard
 * Visualizes analytics data from Firestore
 */

// Initialize Sentry as early as possible
import * as Sentry from "@sentry/browser";

Sentry.init({
  dsn: "https://98f444c73599f68edcf65d9db57c665f@o4510773732245504.ingest.us.sentry.io/4510774427189248",
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
  ],
  _experiments: {
    enableLogs: true,
  },
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.xrgallery\.io/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

import { Logger } from '../src/utils/Logger';
import { getCurrentUser, onAuthChange } from '../src/firebase/auth';

const logger = Logger.getLogger('AnalyticsDashboard');
import { db } from '../src/firebase/config';
import { collection, query, where, getDocs, orderBy, Timestamp, limit } from 'firebase/firestore';
import { listUserSequences, listOrgSequences, type Sequence } from '../src/api/SequenceAPI';
import { listUserOrganizations, type Organization } from '../src/api/OrganizationAPI';
import { getUserProfile, ensureUserProfile, hasFeatureAccess, toggleUserAnalytics, type UserProfile } from '../src/api/UserProfileAPI';
import type { SessionAnalytics, StageViewAnalytics, AnalyticsEvent, AnalyticsEventType } from '../src/analytics/types';
import { Chart, registerables } from 'chart.js';
import { showUpgradeModal } from '../src/ui/UpgradeModal';
import { showAlert } from '../src/ui/ModalManager';
import { isFeatureEnabled } from '../src/config/featureFlags';
import { initToolbar, updateToolbarProfile, triggerToolbarAnimations } from '../src/ui/UserDropdown';

// Register Chart.js components
Chart.register(...registerables);

// State
let currentUserId: string | null = null;
let currentUserProfile: UserProfile | null = null;
let hasFullAnalyticsAccess: boolean = false;
let sequences: Sequence[] = [];
let organizations: Organization[] = [];
let selectedSequenceId: string | null = null;
let dateRangeType: string = '7days';
let startDate: Date | null = null;
let endDate: Date | null = null;

// Current analytics data for export
let currentSessions: SessionAnalytics[] = [];
let currentStageViews: StageViewAnalytics[] = [];

// Charts
let sessionsChart: Chart | null = null;
let deviceChart: Chart | null = null;
let stagePerformanceChart: Chart | null = null;

// DOM Elements
const sequenceSelector = document.getElementById('sequenceSelector') as HTMLSelectElement;
const exportBtn = document.getElementById('exportBtn') as HTMLButtonElement | null;
const dateRangeSelect = document.getElementById('dateRange') as HTMLSelectElement;
const startDateInput = document.getElementById('startDate') as HTMLInputElement;
const endDateInput = document.getElementById('endDate') as HTMLInputElement;
const startDateGroup = document.getElementById('startDateGroup') as HTMLElement;
const endDateGroup = document.getElementById('endDateGroup') as HTMLElement;
const applyFilterBtn = document.getElementById('applyFilterBtn') as HTMLButtonElement;
const loadingState = document.getElementById('loadingState') as HTMLElement;
const analyticsContent = document.getElementById('analyticsContent') as HTMLElement;
// Note: settingsBtn, signOutBtn, usageBtn are now handled by shared initToolbar
const userSettingsModal = document.getElementById('user-settings-modal') as HTMLElement;
const userSettingsForm = document.getElementById('user-settings-form') as HTMLFormElement;
const cancelUserSettingsBtn = document.getElementById('cancel-user-settings-btn') as HTMLButtonElement;
const userAnalyticsCheckbox = document.getElementById('user-analytics-enabled') as HTMLInputElement;

const userEmailSpan = document.getElementById('user-email') as HTMLElement;
const mobileUserEmailSpan = document.getElementById('mobile-user-email') as HTMLElement;
const upgradeBtn = document.getElementById('upgradeBtn') as HTMLButtonElement | null;
const proBadge = document.getElementById('pro-badge') as HTMLElement | null;

const userDropdown = document.getElementById('user-dropdown') as HTMLElement;
const userDropdownToggle = document.getElementById('user-dropdown-toggle') as HTMLElement;

// Stat elements
const totalSessionsEl = document.getElementById('totalSessions') as HTMLElement;
const uniqueUsersEl = document.getElementById('uniqueUsers') as HTMLElement;
const vrSessionsEl = document.getElementById('vrSessions') as HTMLElement;
const avgDurationEl = document.getElementById('avgDuration') as HTMLElement;
const avgFpsEl = document.getElementById('avgFps') as HTMLElement;
const totalStageViewsEl = document.getElementById('totalStageViews') as HTMLElement;

// Table elements
const topStagesTable = document.getElementById('topStagesTable') as HTMLElement;
const recentSessionsTable = document.getElementById('recentSessionsTable') as HTMLElement;

/**
 * Initialize dashboard
 */
async function init() {
  try {
    // Wait for auth state to be ready
    const currentUser = await waitForAuth();

    if (!currentUser) {
      logger.warn('No user authenticated, redirecting');
      // Trigger animations before redirect so UI isn't broken if redirect fails
      triggerToolbarAnimations('header-toolbar-animate');
      window.location.href = '/dashboard/auth.html';
      return;
    }

    logger.info('User authenticated:', currentUser.email);

    // Store user ID
    currentUserId = currentUser.uid;

    // Load user profile and check tier (backfills missing fields for existing users)
    currentUserProfile = await ensureUserProfile(currentUserId!, currentUser.email, currentUser.displayName);
    if (currentUserProfile) {
      hasFullAnalyticsAccess = hasFeatureAccess(currentUserProfile, 'full_analytics');
      logger.debug(`Analytics access: ${hasFullAnalyticsAccess ? 'Full (Pro/Admin)' : 'Limited (Free)'}`);

      // Initialize shared toolbar (dropdown, usage, settings, signout, tier badge)
      initToolbar({
        userProfile: currentUserProfile,
        userId: currentUser.uid,
        animationClass: 'header-toolbar-animate',
        triggerAnimations: false, // We'll trigger after sequences load
      });

      // Update user email in both desktop and mobile
      if (userEmailSpan) {
        userEmailSpan.textContent = currentUser.email || 'User';
      }
      if (mobileUserEmailSpan) {
        mobileUserEmailSpan.textContent = currentUser.email || 'User';
      }

      // Show/hide admin dashboard button based on tier
      const adminDashboardBtn = document.getElementById('admin-dashboard-btn');
      if (adminDashboardBtn) {
        adminDashboardBtn.style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
      }

      // Show/hide newsletter admin button based on tier
      const newsletterAdminBtn = document.getElementById('newsletter-admin-btn');
      if (newsletterAdminBtn) {
        newsletterAdminBtn.style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
      }

      // Show/hide blog admin button based on tier
      const blogAdminBtn = document.getElementById('blog-admin-btn');
      if (blogAdminBtn) {
        blogAdminBtn.style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
      }

      // Show/hide admin items in mobile menu
      document.querySelectorAll('.admin-mobile-btn').forEach((el) => {
        (el as HTMLElement).style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
      });

      // Show/hide admin items in dropdown menu
      document.querySelectorAll('.admin-dropdown-btn').forEach((el) => {
        (el as HTMLElement).style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
      });
    }

    // Trigger toolbar animations NOW - we have user profile/tier/email info
    // Don't wait for sequences/analytics data to load
    triggerToolbarAnimations('header-toolbar-animate');

    // Setup event listeners
    setupEventListeners();

    // Load user's own sequences
    const userSequences = await listUserSequences(currentUserId!);

    // Load organization sequences
    organizations = await listUserOrganizations(currentUserId!);
    const orgSequences: Sequence[] = [];

    for (const org of organizations) {
      if (org.id) {
        const orgSeqs = await listOrgSequences(org.id);
        orgSequences.push(...orgSeqs);
      }
    }

    // Combine all sequences
    sequences = [...userSequences, ...orgSequences];

    logger.info(`Loaded ${userSequences.length} user sequences and ${orgSequences.length} org sequences from ${organizations.length} organizations`);

    renderSequenceSelector();

    // Load analytics data
    await loadAnalytics();

    logger.info('Analytics dashboard initialized');
  } catch (error) {
    logger.error('Failed to initialize analytics dashboard:', error);
    showAlert('Failed to load analytics. Please try again.', 'Error', '❌');
    // Still trigger animations on error so UI isn't stuck invisible
    triggerToolbarAnimations('header-toolbar-animate');
  }
}

/**
 * Wait for auth state to be ready
 */
function waitForAuth(): Promise<any> {
  return new Promise((resolve) => {
    const user = getCurrentUser();
    if (user) {
      resolve(user);
      return;
    }

    const unsubscribe = onAuthChange((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
  // Hide pricing navigation buttons if feature is disabled
  if (!isFeatureEnabled('PRICING_PAGE')) {
    document.querySelectorAll('.pricing-nav-btn').forEach((btn) => {
      (btn as HTMLElement).style.display = 'none';
    });
  }

  // Note: signOut, settings, usage button handlers are now set up by initToolbar

  // Header upgrade button - show upgrade modal
  // Always attach click handler - showUpgradeModal handles feature flag internally
  if (upgradeBtn) {
    upgradeBtn.addEventListener('click', async () => {
      if (currentUserId) {
        await showUpgradeModal(currentUserId, currentUserProfile?.email);
      } else {
        window.location.href = '/dashboard/auth.html';
      }
    });
  }

  if (cancelUserSettingsBtn) {
    cancelUserSettingsBtn.addEventListener('click', () => {
      userSettingsModal.classList.remove('show');
    });
  }

  if (userSettingsModal) {
    userSettingsModal.addEventListener('click', (e) => {
      if (e.target === userSettingsModal) {
        userSettingsModal.classList.remove('show');
      }
    });
  }

  if (userSettingsForm) {
    userSettingsForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!currentUserId) return;

      try {
        const enabled = userAnalyticsCheckbox.checked;
        await toggleUserAnalytics(currentUserId, enabled);
        
        // Update local profile
        if (currentUserProfile) {
          currentUserProfile.analyticsEnabled = enabled;
        }

        userSettingsModal.classList.remove('show');
        showAlert('Settings saved successfully.', 'Success', '✅');
      } catch (error) {
        logger.error('Failed to save settings:', error);
        showAlert('Failed to save settings.', 'Error', '❌');
      }
    });
  }

  if (exportBtn) {
    exportBtn.addEventListener('click', handleExport);
  }

  sequenceSelector.addEventListener('change', () => {
    selectedSequenceId = sequenceSelector.value || null;
    loadAnalytics();
  });

  dateRangeSelect.addEventListener('change', () => {
    dateRangeType = dateRangeSelect.value;
    if (dateRangeType === 'custom') {
      startDateGroup.style.display = 'flex';
      endDateGroup.style.display = 'flex';
    } else {
      startDateGroup.style.display = 'none';
      endDateGroup.style.display = 'none';
      loadAnalytics();
    }
  });

  applyFilterBtn.addEventListener('click', () => {
    if (dateRangeType === 'custom') {
      if (startDateInput.value && endDateInput.value) {
        startDate = new Date(startDateInput.value);
        endDate = new Date(endDateInput.value);
        endDate.setHours(23, 59, 59, 999);
        loadAnalytics();
      } else {
        showAlert('Please select both start and end dates.', 'Invalid Date Range', '📅');
      }
    }
  });

  // Listen for tier updates from Pac-Man game
  window.addEventListener('tier-updated', async () => {
    logger.info('Tier updated event received! Refreshing analytics...');
    // Reload the entire analytics view to update locked cards
    window.location.reload();
  });

  // Note: User dropdown toggle is now set up by initToolbar
}

/**
 * Render sequence selector
 */
function renderSequenceSelector() {
  // Separate user sequences and org sequences
  const userSeqs = sequences.filter(seq => seq.userId === currentUserId && !seq.orgId);
  const orgSeqs = sequences.filter(seq => seq.orgId);

  // Group org sequences by orgId
  const orgGroups = new Map<string, Sequence[]>();
  orgSeqs.forEach(seq => {
    if (seq.orgId) {
      if (!orgGroups.has(seq.orgId)) {
        orgGroups.set(seq.orgId, []);
      }
      orgGroups.get(seq.orgId)!.push(seq);
    }
  });

  let optionsHtml = '<option value="">All Sequences</option>';

  // Add user sequences
  if (userSeqs.length > 0) {
    optionsHtml += '<optgroup label="My Tours">';
    userSeqs.forEach(seq => {
      optionsHtml += `<option value="${seq.id}">${seq.name}</option>`;
    });
    optionsHtml += '</optgroup>';
  }

  // Add org sequences grouped by organization
  if (orgGroups.size > 0) {
    orgGroups.forEach((seqs, orgId) => {
      // Find organization name
      const org = organizations.find(o => o.id === orgId);
      const orgName = org ? org.name : `Organization (${orgId.substring(0, 8)})`;
      optionsHtml += `<optgroup label="${orgName}">`;
      seqs.forEach(seq => {
        optionsHtml += `<option value="${seq.id}">${seq.name}</option>`;
      });
      optionsHtml += '</optgroup>';
    });
  }

  sequenceSelector.innerHTML = optionsHtml;
}

/**
 * Get date range
 */
function getDateRange(): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();

  if (dateRangeType === 'custom' && startDate && endDate) {
    return { start: startDate, end: endDate };
  }

  switch (dateRangeType) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'yesterday':
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() - 1);
      break;
    case '7days':
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      break;
    case '30days':
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      break;
    case '90days':
      start.setDate(start.getDate() - 90);
      start.setHours(0, 0, 0, 0);
      break;
  }

  return { start, end };
}

/**
 * Load analytics data from analytics_events collection and aggregate
 */
async function loadAnalytics() {
  try {
    loadingState.style.display = 'block';
    analyticsContent.style.display = 'none';

    const { start, end } = getDateRange();
    const startTimestamp = Timestamp.fromDate(start);
    const endTimestamp = Timestamp.fromDate(end);

    const events: AnalyticsEvent[] = [];

    if (selectedSequenceId) {
      // Fetch events for a specific sequence
      const eventsQuery = query(
        collection(db, 'analytics_events'),
        where('sequenceId', '==', selectedSequenceId),
        where('timestamp', '>=', startTimestamp),
        where('timestamp', '<=', endTimestamp),
        orderBy('timestamp', 'desc')
      );

      const eventsSnapshot = await getDocs(eventsQuery);
      eventsSnapshot.forEach((doc) => {
        events.push({ id: doc.id, ...doc.data() } as unknown as AnalyticsEvent);
      });
    } else {
      // Fetch events for all user's sequences (including org sequences)
      const sequenceIds = sequences.map(seq => seq.id).filter((id): id is string => id !== undefined);

      logger.debug(`Fetching analytics for ${sequenceIds.length} sequences`);

      if (sequenceIds.length === 0) {
        logger.warn('No sequences found');
      } else {
        // Fetch in batches of 10 (Firestore 'in' operator limit)
        for (let i = 0; i < sequenceIds.length; i += 10) {
          const batch = sequenceIds.slice(i, i + 10);

          const eventsQuery = query(
            collection(db, 'analytics_events'),
            where('sequenceId', 'in', batch),
            where('timestamp', '>=', startTimestamp),
            where('timestamp', '<=', endTimestamp),
            orderBy('timestamp', 'desc')
          );

          const eventsSnapshot = await getDocs(eventsQuery);
          eventsSnapshot.forEach((doc) => {
            events.push({ id: doc.id, ...doc.data() } as unknown as AnalyticsEvent);
          });
        }
      }
    }

    logger.debug(`Loaded ${events.length} raw events`);

    // Aggregate events into sessions and stage views
    const { sessions, stageViews } = aggregateEvents(events);

    logger.debug(`Aggregated into ${sessions.length} sessions and ${stageViews.length} stage views`);

    // Debug: Log if no data found
    if (events.length === 0) {
      logger.warn('No analytics events found for the selected time period and sequences.');
      logger.debug('Selected sequence ID:', selectedSequenceId || 'All sequences');
      logger.debug('Date range:', start.toLocaleDateString(), 'to', end.toLocaleDateString());
      logger.debug('Number of sequences:', sequences.length);
      logger.debug('Sequence IDs:', sequences.map(s => s.id).join(', '));
    }

    // Store for export
    currentSessions = sessions;
    currentStageViews = stageViews;

    // Calculate metrics
    calculateMetrics(sessions, stageViews);

    // Render charts
    renderCharts(sessions, stageViews);

    // Render tables
    renderTables(sessions, stageViews);

    loadingState.style.display = 'none';
    analyticsContent.style.display = 'block';
  } catch (error) {
    logger.error('Failed to load analytics:', error);
    showAlert('Failed to load analytics data. Please try again.', 'Error', '❌');
    loadingState.style.display = 'none';
  }
}

/**
 * Aggregate raw events into sessions and stage views
 */
function aggregateEvents(events: AnalyticsEvent[]): { sessions: SessionAnalytics[]; stageViews: StageViewAnalytics[] } {
  const sessionsMap = new Map<string, SessionAnalytics>();
  const stageViewsMap = new Map<string, StageViewAnalytics>();
  const sessionFpsValues = new Map<string, number[]>();
  const stageViewFpsValues = new Map<string, number[]>();
  const sessionStartTimes = new Map<string, number>();
  const sessionEndTimes = new Map<string, number>();
  const stageViewStartTimes = new Map<string, number>();
  const stageViewEndTimes = new Map<string, number>();

  // Sort events by timestamp to process in chronological order
  const sortedEvents = [...events].sort((a, b) => {
    const aTime = a.timestamp.toMillis();
    const bTime = b.timestamp.toMillis();
    return aTime - bTime;
  });

  sortedEvents.forEach(event => {
    const sessionKey = event.sessionId;
    const eventTime = event.timestamp.toMillis();

    // Track session start/end times
    if (event.eventType === 'session_started') {
      sessionStartTimes.set(sessionKey, eventTime);
    } else if (event.eventType === 'session_ended') {
      sessionEndTimes.set(sessionKey, eventTime);
    }

    // Aggregate session data
    if (!sessionsMap.has(sessionKey)) {
      sessionsMap.set(sessionKey, {
        id: sessionKey,
        sessionId: event.sessionId,
        sequenceId: event.sequenceId,
        userId: event.userId,
        timestamp: event.timestamp,
        deviceInfo: event.deviceInfo,
        isVR: false,
        duration: 0,
        stagesViewed: 0,
        avgFps: 0
      });
      sessionFpsValues.set(sessionKey, []);
    }

    const session = sessionsMap.get(sessionKey)!;

    // Update session based on event type
    switch (event.eventType) {
      case 'vr_session_started':
        session.isVR = true;
        break;
      case 'session_ended':
        // Calculate duration from start/end times if available
        const startTime = sessionStartTimes.get(sessionKey);
        const endTime = sessionEndTimes.get(sessionKey);
        if (startTime && endTime) {
          session.duration = endTime - startTime;
        } else if (event.eventData && 'duration' in event.eventData) {
          session.duration = event.eventData.duration as number;
        }
        break;
      case 'stage_view_started':
        session.stagesViewed = (session.stagesViewed || 0) + 1;
        // Create stage view entry
        if (event.eventData && 'stageIndex' in event.eventData) {
          const stageKey = `${sessionKey}_${event.eventData.stageIndex}`;
          stageViewStartTimes.set(stageKey, eventTime);

          if (!stageViewsMap.has(stageKey)) {
            const stageName = ('stageName' in event.eventData && event.eventData.stageName)
              ? event.eventData.stageName as string
              : `Scene ${(event.eventData.stageIndex as number) + 1}`;
            stageViewsMap.set(stageKey, {
              id: stageKey,
              sessionId: event.sessionId,
              sequenceId: event.sequenceId,
              stageIndex: event.eventData.stageIndex as number,
              stageName: stageName,
              timestamp: event.timestamp,
              duration: 0,
              avgFps: 0
            });
            stageViewFpsValues.set(stageKey, []);
          }
        }
        break;
      case 'stage_view_ended':
        if (event.eventData && 'stageIndex' in event.eventData) {
          const stageKey = `${sessionKey}_${event.eventData.stageIndex}`;
          stageViewEndTimes.set(stageKey, eventTime);

          const stageView = stageViewsMap.get(stageKey);
          if (stageView) {
            // Try to get duration from event data first
            if ('duration' in event.eventData) {
              stageView.duration = event.eventData.duration as number;
            } else {
              // Calculate from start/end times
              const startTime = stageViewStartTimes.get(stageKey);
              if (startTime) {
                stageView.duration = eventTime - startTime;
              }
            }

            if ('avgFps' in event.eventData) {
              stageView.avgFps = event.eventData.avgFps as number;
            }
          }
        }
        break;
      case 'performance_metric':
        // Collect FPS values for averaging
        if (event.eventData && 'fps' in event.eventData) {
          const fps = event.eventData.fps as number;
          sessionFpsValues.get(sessionKey)?.push(fps);

          // Also add to stage view if applicable
          if ('stageIndex' in event.eventData) {
            const stageKey = `${sessionKey}_${event.eventData.stageIndex}`;
            stageViewFpsValues.get(stageKey)?.push(fps);
          }
        }
        break;
    }
  });

  // Calculate average FPS for sessions
  sessionFpsValues.forEach((fpsValues, sessionKey) => {
    const session = sessionsMap.get(sessionKey);
    if (session && fpsValues.length > 0) {
      session.avgFps = Math.round(fpsValues.reduce((a, b) => a + b, 0) / fpsValues.length);
    }
  });

  // Calculate average FPS for stage views
  stageViewFpsValues.forEach((fpsValues, stageKey) => {
    const stageView = stageViewsMap.get(stageKey);
    if (stageView && fpsValues.length > 0) {
      stageView.avgFps = Math.round(fpsValues.reduce((a, b) => a + b, 0) / fpsValues.length);
    }
  });

  // Calculate duration for sessions that don't have session_ended event
  sessionsMap.forEach((session, sessionKey) => {
    if (session.duration === 0) {
      const startTime = sessionStartTimes.get(sessionKey);
      if (startTime) {
        // Use last event time as end time
        const sessionEvents = sortedEvents.filter(e => e.sessionId === sessionKey);
        if (sessionEvents.length > 0) {
          const lastEventTime = sessionEvents[sessionEvents.length - 1].timestamp.toMillis();
          session.duration = lastEventTime - startTime;
        }
      }
    }
  });

  // Calculate duration for stage views that don't have stage_view_ended event
  stageViewsMap.forEach((stageView, stageKey) => {
    if (stageView.duration === 0) {
      const startTime = stageViewStartTimes.get(stageKey);
      if (startTime) {
        // Check if there's an explicit end time
        const endTime = stageViewEndTimes.get(stageKey);
        if (endTime) {
          stageView.duration = endTime - startTime;
        } else {
          // Use the next stage_view_started event or last event in the session as end time
          const sessionEvents = sortedEvents.filter(e => e.sessionId === stageView.sessionId);

          // Find the next stage view start after this one
          let nextStageStartTime: number | null = null;
          for (const event of sessionEvents) {
            if (event.eventType === 'stage_view_started' &&
                event.eventData &&
                'stageIndex' in event.eventData &&
                event.eventData.stageIndex !== stageView.stageIndex) {
              const eventTime = event.timestamp.toMillis();
              if (eventTime > startTime) {
                nextStageStartTime = eventTime;
                break;
              }
            }
          }

          // Use next stage start or last session event as end time
          if (nextStageStartTime) {
            stageView.duration = nextStageStartTime - startTime;
          } else if (sessionEvents.length > 0) {
            const lastEventTime = sessionEvents[sessionEvents.length - 1].timestamp.toMillis();
            if (lastEventTime > startTime) {
              stageView.duration = lastEventTime - startTime;
            }
          }
        }
      }
    }
  });

  return {
    sessions: Array.from(sessionsMap.values()),
    stageViews: Array.from(stageViewsMap.values())
  };
}

/**
 * Calculate metrics
 */
function calculateMetrics(sessions: SessionAnalytics[], stageViews: StageViewAnalytics[]) {
  // Total sessions (always shown)
  totalSessionsEl.textContent = sessions.length.toString();

  if (!hasFullAnalyticsAccess) {
    // Free tier: Show Pro badges and lock state
    const lockedCards = ['uniqueUsersCard', 'vrSessionsCard', 'avgDurationCard', 'avgFpsCard', 'totalStageViewsCard'];
    const lockedBadges = ['uniqueUsersBadge', 'vrSessionsBadge', 'avgDurationBadge', 'avgFpsBadge', 'totalStageViewsBadge'];

    lockedCards.forEach(cardId => {
      const card = document.getElementById(cardId);
      if (card) {
        card.classList.add('locked');
        card.onclick = () => {
          if (currentUserId) {
            showUpgradeModal(currentUserId, currentUserProfile?.email);
          }
        };
      }
    });

    lockedBadges.forEach(badgeId => {
      const badge = document.getElementById(badgeId);
      if (badge) badge.style.display = 'inline-block';
    });

    // Show locked values
    uniqueUsersEl.textContent = '—';
    vrSessionsEl.textContent = '—';
    avgDurationEl.textContent = '—';
    avgFpsEl.textContent = '—';
    totalStageViewsEl.textContent = '—';

    // Add tooltips
    uniqueUsersEl.title = 'Click to upgrade to Pro';
    vrSessionsEl.title = 'Click to upgrade to Pro';
    avgDurationEl.title = 'Click to upgrade to Pro';
    avgFpsEl.title = 'Click to upgrade to Pro';
    totalStageViewsEl.title = 'Click to upgrade to Pro';
    return;
  }

  // Pro/Admin tier: Show all metrics
  const uniqueSessions = new Set(sessions.map(s => s.sessionId));
  uniqueUsersEl.textContent = uniqueSessions.size.toString();

  const vrSessionsCount = sessions.filter(s => s.isVR).length;
  vrSessionsEl.textContent = vrSessionsCount.toString();

  const durations = sessions
    .filter(s => s.duration !== undefined)
    .map(s => s.duration || 0);
  const avgDuration = durations.length > 0
    ? durations.reduce((a, b) => a + b, 0) / durations.length
    : 0;
  avgDurationEl.textContent = formatDuration(avgDuration);

  const fpsValues = sessions
    .filter(s => s.avgFps !== undefined)
    .map(s => s.avgFps || 0);
  const avgFps = fpsValues.length > 0
    ? fpsValues.reduce((a, b) => a + b, 0) / fpsValues.length
    : 0;
  avgFpsEl.textContent = Math.round(avgFps).toString();

  totalStageViewsEl.textContent = stageViews.length.toString();
}

/**
 * Render charts
 */
function renderCharts(sessions: SessionAnalytics[], stageViews: StageViewAnalytics[]) {
  const chartsSection = document.querySelector('.charts-section') as HTMLElement;

  if (!hasFullAnalyticsAccess) {
    // Free tier: Show charts but with upgrade overlay
    if (chartsSection) {
      chartsSection.style.display = 'grid';
      chartsSection.classList.add('upgrade-overlay');

      // Add upgrade prompt if not already added
      if (!chartsSection.querySelector('.upgrade-prompt')) {
        const prompt = document.createElement('div');
        prompt.className = 'upgrade-prompt';
        prompt.innerHTML = `
          <h3>🔒 Unlock Full Analytics</h3>
          <p>Upgrade to Pro to see detailed charts and visualizations of your VR experiences.</p>
          <button class="upgrade-prompt-btn" id="chartsUpgradeBtn">Upgrade to Pro</button>
        `;
        chartsSection.appendChild(prompt);

        // Add click handler - show upgrade modal
        const upgradeBtn = prompt.querySelector('#chartsUpgradeBtn');
        upgradeBtn?.addEventListener('click', async () => {
          if (currentUserId) {
            await showUpgradeModal(currentUserId, currentUserProfile?.email);
          } else {
            window.location.href = '/dashboard/auth.html';
          }
        });
      }
    }
    return;
  }

  // Pro/Admin: Show charts without overlay
  if (chartsSection) {
    chartsSection.style.display = 'grid';
    chartsSection.classList.remove('upgrade-overlay');
    const existingPrompt = chartsSection.querySelector('.upgrade-prompt');
    if (existingPrompt) existingPrompt.remove();
  }

  renderSessionsChart(sessions);
  renderDeviceChart(sessions);
  renderStagePerformanceChart(stageViews);
}

/**
 * Render sessions over time chart
 */
function renderSessionsChart(sessions: SessionAnalytics[]) {
  const ctx = document.getElementById('sessionsChart') as HTMLCanvasElement;

  if (sessionsChart) {
    sessionsChart.destroy();
  }

  // Group sessions by date
  const sessionsByDate = new Map<string, number>();
  sessions.forEach(session => {
    const date = session.timestamp.toDate().toLocaleDateString();
    sessionsByDate.set(date, (sessionsByDate.get(date) || 0) + 1);
  });

  const labels = Array.from(sessionsByDate.keys()).sort();
  const data = labels.map(label => sessionsByDate.get(label) || 0);

  sessionsChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Sessions',
        data,
        borderColor: '#c62828',
        backgroundColor: 'rgba(198, 40, 40, 0.1)',
        tension: 0.4,
        fill: true
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        }
      }
    }
  });
}

/**
 * Render device distribution chart
 */
function renderDeviceChart(sessions: SessionAnalytics[]) {
  const ctx = document.getElementById('deviceChart') as HTMLCanvasElement;

  if (deviceChart) {
    deviceChart.destroy();
  }

  // Count devices
  const deviceCounts = new Map<string, number>();
  sessions.forEach(session => {
    const device = session.deviceInfo?.type || 'unknown';
    deviceCounts.set(device, (deviceCounts.get(device) || 0) + 1);
  });

  const labels = Array.from(deviceCounts.keys());
  const data = labels.map(label => deviceCounts.get(label) || 0);

  deviceChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: labels.map(l => l.charAt(0).toUpperCase() + l.slice(1)),
      datasets: [{
        data,
        backgroundColor: [
          '#c62828',
          '#f44336',
          '#e57373',
          '#ffcdd2'
        ]
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }
  });
}

/**
 * Render stage performance chart
 */
function renderStagePerformanceChart(stageViews: StageViewAnalytics[]) {
  const ctx = document.getElementById('stagePerformanceChart') as HTMLCanvasElement;

  if (stagePerformanceChart) {
    stagePerformanceChart.destroy();
  }

  // Group by stage index
  const stageStats = new Map<number, { views: number; totalDuration: number; totalFps: number }>();

  stageViews.forEach(view => {
    const stats = stageStats.get(view.stageIndex) || { views: 0, totalDuration: 0, totalFps: 0 };
    stats.views++;
    stats.totalDuration += view.duration || 0;
    stats.totalFps += view.avgFps || 0;
    stageStats.set(view.stageIndex, stats);
  });

  const labels = Array.from(stageStats.keys()).sort((a, b) => a - b).map(i => `Scene ${i + 1}`);
  const viewsData = labels.map((_, i) => stageStats.get(i)?.views || 0);
  const avgDurationData = labels.map((_, i) => {
    const stats = stageStats.get(i);
    return stats ? (stats.totalDuration / stats.views) / 1000 : 0;
  });

  stagePerformanceChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'Views',
          data: viewsData,
          backgroundColor: 'rgba(198, 40, 40, 0.8)',
          yAxisID: 'y'
        },
        {
          label: 'Avg Duration (s)',
          data: avgDurationData,
          backgroundColor: 'rgba(99, 132, 255, 0.8)',
          yAxisID: 'y1'
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          beginAtZero: true,
          title: {
            display: true,
            text: 'Views'
          }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          beginAtZero: true,
          title: {
            display: true,
            text: 'Duration (seconds)'
          },
          grid: {
            drawOnChartArea: false
          }
        }
      }
    }
  });
}

/**
 * Render tables
 */
function renderTables(sessions: SessionAnalytics[], stageViews: StageViewAnalytics[]) {
  const tablesSection = document.querySelector('.tables-grid') as HTMLElement;

  if (!hasFullAnalyticsAccess) {
    // Free tier: Show tables but with upgrade overlay
    if (tablesSection) {
      tablesSection.style.display = 'grid';
      tablesSection.classList.add('upgrade-overlay');

      // Add upgrade prompt if not already added
      if (!tablesSection.querySelector('.upgrade-prompt')) {
        const prompt = document.createElement('div');
        prompt.className = 'upgrade-prompt';
        prompt.innerHTML = `
          <h3>🔒 Unlock Detailed Tables</h3>
          <p>Upgrade to Pro to see detailed breakdowns of stage performance and user sessions.</p>
          <button class="upgrade-prompt-btn" id="tablesUpgradeBtn">Upgrade to Pro</button>
        `;
        tablesSection.appendChild(prompt);

        // Add click handler - show upgrade modal
        const upgradeBtn = prompt.querySelector('#tablesUpgradeBtn');
        upgradeBtn?.addEventListener('click', async () => {
          if (currentUserId) {
            await showUpgradeModal(currentUserId, currentUserProfile?.email);
          } else {
            window.location.href = '/dashboard/auth.html';
          }
        });
      }
    }
    return;
  }

  // Pro/Admin: Show tables without overlay
  if (tablesSection) {
    tablesSection.style.display = 'grid';
    tablesSection.classList.remove('upgrade-overlay');
    const existingPrompt = tablesSection.querySelector('.upgrade-prompt');
    if (existingPrompt) existingPrompt.remove();
  }

  renderTopStagesTable(stageViews);
  renderRecentSessionsTable(sessions);
}

/**
 * Render top stages table
 */
function renderTopStagesTable(stageViews: StageViewAnalytics[]) {
  // Group by stage
  const stageStats = new Map<number, { views: number; totalDuration: number; fpsValues: number[]; completions: number }>();

  stageViews.forEach(view => {
    const stats = stageStats.get(view.stageIndex) || { views: 0, totalDuration: 0, fpsValues: [], completions: 0 };
    stats.views++;
    stats.totalDuration += view.duration || 0;
    if (view.avgFps) stats.fpsValues.push(view.avgFps);
    // Count as completion if duration > 5 seconds
    if ((view.duration || 0) > 5000) stats.completions++;
    stageStats.set(view.stageIndex, stats);
  });

  const rows = Array.from(stageStats.entries())
    .map(([index, stats]) => ({
      index,
      views: stats.views,
      avgDuration: stats.totalDuration / stats.views,
      completionRate: (stats.completions / stats.views) * 100,
      avgFps: stats.fpsValues.length > 0
        ? stats.fpsValues.reduce((a, b) => a + b, 0) / stats.fpsValues.length
        : 0
    }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10);

  if (rows.length === 0) {
    topStagesTable.innerHTML = '<tr><td colspan="5" style="text-align: center; color: #999;">No data available</td></tr>';
    return;
  }

  topStagesTable.innerHTML = rows.map(row => `
    <tr>
      <td><strong>Scene ${row.index + 1}</strong></td>
      <td>${row.views}</td>
      <td>${formatDuration(row.avgDuration)}</td>
      <td>${row.completionRate.toFixed(1)}%</td>
      <td>${Math.round(row.avgFps)}</td>
    </tr>
  `).join('');
}

/**
 * Render recent sessions table
 */
function renderRecentSessionsTable(sessions: SessionAnalytics[]) {
  const recentSessions = sessions.slice(0, 20);

  if (recentSessions.length === 0) {
    recentSessionsTable.innerHTML = '<tr><td colspan="6" style="text-align: center; color: #999;">No data available</td></tr>';
    return;
  }

  recentSessionsTable.innerHTML = recentSessions.map(session => {
    const stagesViewed = session.stagesViewed || 0;
    return `
      <tr>
        <td>${session.timestamp.toDate().toLocaleString()}</td>
        <td>${session.deviceInfo?.type || 'Unknown'}</td>
        <td>${session.deviceInfo?.browser || 'Unknown'}</td>
        <td>${session.isVR ? '<span class="badge success">VR</span>' : '<span class="badge">2D</span>'}</td>
        <td>${formatDuration(session.duration || 0)}</td>
        <td>${stagesViewed}</td>
      </tr>
    `;
  }).join('');
}

/**
 * Handle export - generates CSV of analytics data
 */
function handleExport() {
  if (currentSessions.length === 0) {
    showAlert('No Data', 'No analytics data available to export. Try selecting a different date range.');
    return;
  }

  // Build CSV content
  const headers = [
    'Date',
    'Session ID',
    'Sequence',
    'Device Type',
    'Browser',
    'VR Mode',
    'Duration (seconds)',
    'Stages Viewed',
    'Avg FPS'
  ];

  const rows = currentSessions.map(session => {
    const sequenceName = sequences.find(s => s.id === session.sequenceId)?.name || session.sequenceId;
    return [
      session.timestamp.toDate().toISOString(),
      session.sessionId,
      `"${sequenceName.replace(/"/g, '""')}"`,
      session.deviceInfo?.type || 'Unknown',
      session.deviceInfo?.browser || 'Unknown',
      session.isVR ? 'Yes' : 'No',
      Math.round((session.duration || 0) / 1000),
      session.stagesViewed || 0,
      Math.round(session.avgFps || 0)
    ].join(',');
  });

  const csvContent = [headers.join(','), ...rows].join('\n');

  // Create download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  const dateStr = new Date().toISOString().split('T')[0];
  const sequenceSuffix = selectedSequenceId
    ? `-${sequences.find(s => s.id === selectedSequenceId)?.name || 'tour'}`
    : '-all';

  link.href = url;
  link.download = `xrgallery-analytics${sequenceSuffix}-${dateStr}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  logger.info(`Exported ${currentSessions.length} sessions to CSV`);
}

/**
 * Format duration
 */
function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

// triggerToolbarAnimations, setBadgeTier, updateProBadge moved to shared UserDropdown.ts

/**
 * Open user settings modal
 */
async function openUserSettings() {
  if (!currentUserProfile) return;

  try {
    // Set checkbox state
    if (userAnalyticsCheckbox) {
      userAnalyticsCheckbox.checked = currentUserProfile.analyticsEnabled !== false;
    }

    userSettingsModal.classList.add('show');
  } catch (error) {
    logger.error('Error opening settings:', error);
  }
}

// Initialize on load
init();
